/**
 * Borrador o público, como una elección de dos y no como una etiqueta.
 *
 * Lo usan las dos listas que publican —servicios reservables y ofertas— y la
 * ficha del servicio: es la misma decisión sobre el mismo campo, y con un mando
 * por pantalla acababa contándose de tres maneras.
 *
 * Antes era un chip que decía «Borrador» y que, al pulsarlo, cambiaba de estado.
 * Un chip de estado es lo que las tablas usan para NO poder tocarse, así que no
 * se veía que ahí hubiera nada que elegir —y quien lo descubría lo descubría
 * cambiándolo sin querer—. Ahora abre las dos opciones con la puesta marcada.
 *
 * Las dos opciones van desnudas: dos palabras que ya se explican solas. Una
 * línea de ayuda bajo cada una convertía un mando de dos estados en un texto
 * que hay que leer entero para elegir lo que se sabía antes de abrirlo.
 *
 * Y lo que impide publicar tampoco vive aquí dentro. La regla es del servidor
 * —`assertCanPublish`— y sale al elegir «Público»: en un diálogo, que es donde
 * cabe explicar por qué y poner el botón que lo arregla. El menú se queda
 * contestando lo único que se le preguntó — en cuál estoy y qué hay.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconArrowUpRight, IconCheck, IconChevronDown, IconInfoCircle, IconLoader2, IconLock, IconPencil, IconWorld, IconX } from '@tabler/icons-react'

import { StripeMark } from './StripeMark'
import { useI18n } from '../../../lib/i18n'
import { STATUS_KEY } from './offerShared'
import { PlanLimitModal } from '../PlanLimitModal'
import type { PublishBlock } from './offerShared'

/**
 * El diálogo que explica por qué no se publica.
 *
 * En un portal a `document.body` y no donde se pulsó: nace dentro de una fila de
 * la tabla, y ahí dentro cualquier `overflow` o `z-index` de la lista lo
 * recortaría o lo dejaría por debajo del panel.
 */
export function PublishBlockDialog({ kind, itemType = 'booking', connecting, onConnect, onOpenPlans, onClose }: {
  kind: 'plan' | 'stripe'
  itemType?: 'booking' | 'service' | 'digital'
  connecting: boolean
  onConnect: () => void
  onOpenPlans: () => void
  onClose: () => void
}) {
  const { t } = useI18n()
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey) }
  }, [onClose])

  const plan = kind === 'plan'

  const titleKey = plan
    ? itemType === 'digital'
      ? 'offers.block.digitalPlanTitle'
      : itemType === 'service'
        ? 'offers.block.servicePlanTitle'
        : 'offers.block.planTitle'
    : itemType === 'digital' || itemType === 'service'
      ? 'offers.block.digitalStripeTitle'
      : 'offers.block.stripeTitle'

  const bodyKey = plan
    ? itemType === 'digital'
      ? 'offers.block.digitalPlanBody'
      : 'offers.block.planBody'
    : itemType === 'digital'
      ? 'offers.block.digitalStripeBody'
      : itemType === 'service'
        ? 'offers.block.serviceStripeBody'
        : 'offers.block.stripeBody'

  return createPortal(
    <div className="dash-publish-block-scrim" role="presentation" onClick={onClose}>
      <div
        className="dash-publish-block-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-block-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="dash-publish-block-close"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <IconX size={16} />
        </button>

        <span className={`dash-publish-block-icon${plan ? '' : ' is-stripe'}`} aria-hidden="true">
          {plan ? <IconLock size={20} /> : <StripeMark size={20} />}
        </span>

        <h3 id="publish-block-title">
          {t(titleKey)}
        </h3>

        <p className="dash-publish-block-body">
          {t(bodyKey)}
        </p>
        {/* Lo que NO se pierde por no poder publicar. Sin esta línea el diálogo
            se lee como una puerta cerrada, y lo que hay detrás es un servicio
            que se sigue preparando igual. */}
        <div className="dash-publish-block-callout">
          <IconInfoCircle size={16} className="dash-publish-block-callout-icon" aria-hidden="true" />
          <p>{t('offers.block.aside')}</p>
        </div>

        <div className="dash-publish-block-actions">
          <button type="button" className="dash-secondary-btn" onClick={onClose}>{t('offers.block.notNow')}</button>
          <button
            type="button"
            className="dash-primary-btn"
            disabled={connecting}
            /* Planes por el camino del panel —`onOpenPlans`, que es quien sabe
               a dónde lleva— y no abriendo una pestaña con una dirección
               montada a mano: aquélla se llevaba a quien la pulsa fuera de la
               oferta que estaba publicando, y con la ficha a medias detrás. */
            onClick={plan ? () => { onClose(); onOpenPlans() } : onConnect}
          >
            {plan
              ? <>{t('stripe.seePlans')} <IconArrowUpRight size={15} /></>
              : connecting
                ? <><IconLoader2 size={15} className="dash-spin" /> {t('offers.block.connecting')}</>
                : <><StripeMark size={15} /> {t('stripe.connect')}</>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export function OfferStatusSelect({ status, busy, blocked, compact, label, connecting, itemType = 'booking', liveLabelKey, onSelect, onConnect, onOpenPlans }: {
  status: 'draft' | 'active' | 'archived'
  busy: boolean
  blocked: PublishBlock
  /** En la tabla, donde la columna es estrecha. */
  compact?: boolean
  /** El nombre del servicio, para que el lector de pantalla sepa cuál es. */
  label: string
  connecting?: boolean
  itemType?: 'booking' | 'service' | 'digital'
  /** El nombre del estado a la vista, si no es «Pública» (en el blog, «Publicada» o «Programada»). */
  liveLabelKey?: string | undefined
  onSelect: (status: 'draft' | 'active') => void
  onConnect: () => void
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  /* Hacia arriba cuando abajo no cabe: en la última fila de una lista larga el
     menú se salía de la ventana. */
  const [up, setUp] = useState(false)
  /* Qué motivo se está explicando, o `null` si no hay diálogo abierto. Se
     guarda el motivo ENTERO y no un booleano ni su nombre: el menú se cierra al
     abrirlo —y con él se iría la razón que hay que contar— y el del cupo lleva
     además el plan encima, que es lo que la ventana necesita para ofrecer el
     siguiente. */
  const [explaining, setExplaining] = useState<PublishBlock>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  /**
   * El menú vive en el `body`, no colgado del chip.
   *
   * La lista está dentro de un `.dash-cms-table-scroll` con `overflow-x: auto`,
   * y eso recorta también por abajo: el desplegable de las últimas filas se veía
   * cortado a media opción. Es el mismo caso —y la misma solución— que el de las
   * citas: ver `BookingStatusSelect`.
   */
  const [at, setAt] = useState({ top: 8, left: 8 })
  const live = status === 'active'

  /* Escape cierra, y el foco vuelve al chip: quien abrió con el teclado se
     queda donde estaba y no al principio de la fila. */
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
      wrapRef.current?.querySelector('button')?.focus()
    }
    /* Al desplazar o cambiar de tamaño se cierra en vez de perseguir al chip:
       está en coordenadas de pantalla, así que si la fila se mueve el menú se
       quedaría flotando en mitad de la tabla. */
    const onViewportChange = () => setOpen(false)
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
    }
  }, [open])

  /* Ya pintado, se mide y se ajusta: el alto real es el único que sabe si cabe
     abajo. La cuenta del clic es la primera aproximación, para que no aparezca
     en un sitio y salte a otro. */
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return
    const chip = wrapRef.current?.querySelector<HTMLButtonElement>(':scope > button')
    if (!chip) return
    const ancla = chip.getBoundingClientRect()
    const caja = menuRef.current.getBoundingClientRect()
    const borde = 8
    const debajo = window.innerHeight - ancla.bottom - borde
    const encima = ancla.top - borde
    const arriba = debajo < caja.height + 6 && encima > debajo
    const deseado = arriba ? ancla.top - caja.height - 6 : ancla.bottom + 6
    const top = Math.max(borde, Math.min(deseado, Math.max(borde, window.innerHeight - caja.height - borde)))
    const left = Math.max(borde, Math.min(window.innerWidth - caja.width - borde, ancla.right - caja.width))
    setAt((actual) => (actual.top === top && actual.left === left ? actual : { top, left }))
  }, [open])

  const pick = (next: 'draft' | 'active') => {
    setOpen(false)
    if (next === status) return
    /* Publicar es lo que se para; volver a borrador nunca —retirar algo de la
       venta no puede depender de nada—. Misma regla que el servidor. */
    if (next === 'active' && blocked !== null) { setExplaining(blocked); return }
    onSelect(next)
  }

  return (
    <div
      className={`dash-status-select${compact ? ' is-compact' : ''}${open ? ' is-open' : ''}${up ? ' is-up' : ''}`}
      ref={wrapRef}
      /* La fila entera abre la ficha al pulsarla. Sin esto, elegir un estado
         abría además el servicio que se acababa de cambiar. */
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`dash-offer-status is-select${live ? ' is-active' : ''}`}
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('offers.statusAria', { name: label })}
        onClick={(event) => {
          if (!open) {
            const rect = event.currentTarget.getBoundingClientRect()
            const below = window.innerHeight - rect.bottom - 16
            const arriba = below < 120 && rect.top - 16 > below
            setUp(arriba)
            const ancho = 196
            setAt({
              top: arriba ? Math.max(8, rect.top - 96) : rect.bottom + 6,
              left: Math.max(8, Math.min(window.innerWidth - ancho - 8, rect.right - ancho)),
            })
          }
          setOpen((value) => !value)
        }}
      >
        {busy ? <IconLoader2 className="dash-spin" size={12} /> : (live ? t(liveLabelKey ?? STATUS_KEY.active) : t(STATUS_KEY.draft))}
        <IconChevronDown className="dash-status-select-caret" size={12} aria-hidden="true" />
      </button>

      {open ? createPortal(
        <>
          <div className="dash-status-select-backdrop is-portal" role="presentation" onClick={() => setOpen(false)} />
          <div ref={menuRef} className="dash-status-select-menu is-portal" role="listbox" aria-label={t('offers.statusAria', { name: label })} style={at}>
            <button
              type="button"
              role="option"
              aria-selected={!live}
              className={!live ? 'is-on' : ''}
              onClick={() => pick('draft')}
            >
              <IconPencil size={14} aria-hidden="true" />
              {t(STATUS_KEY.draft)}
              {!live ? <IconCheck size={14} aria-hidden="true" /> : null}
            </button>

            {/* Elegible aunque no se pueda: al pulsarla sale el diálogo con el
                motivo. Apagada no habría dónde contarlo, y ese es justo el
                momento en que alguien quiere saberlo. */}
            <button
              type="button"
              role="option"
              aria-selected={live}
              className={live ? 'is-on' : ''}
              onClick={() => pick('active')}
            >
              <IconWorld size={14} aria-hidden="true" />
              {t(liveLabelKey ?? STATUS_KEY.active)}
              {live ? <IconCheck size={14} aria-hidden="true" /> : null}
            </button>
          </div>
        </>,
        document.body,
      ) : null}

      {/* El cupo tiene su propia ventana: no se arregla conectando nada, se
          arregla subiendo de plan o retirando otra. Es la misma que sale en los
          asistentes y en el tope de páginas, así que se reconoce sin leerla. */}
      {explaining?.kind === 'limit' ? (
        <PlanLimitModal
          resource={t('usage.stat.offers').toLowerCase()}
          kind="offers"
          currentPlan={explaining.plan}
          onClose={() => setExplaining(null)}
          onOpenPlans={() => { setExplaining(null); onOpenPlans() }}
        />
      ) : explaining ? (
        <PublishBlockDialog
          kind={explaining.kind}
          itemType={itemType}
          connecting={connecting ?? false}
          onConnect={() => { setExplaining(null); onConnect() }}
          onOpenPlans={() => { setExplaining(null); onOpenPlans() }}
          onClose={() => setExplaining(null)}
        />
      ) : null}
    </div>
  )
}
